import mixpanel from 'mixpanel-browser';
import { Inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ReplaySubject, combineLatest } from 'rxjs';
import { filter, takeUntil, map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { NavigationDataService } from '../data-services/navigation/navigation-data.service';
import { UserPortfolioPreferencesService } from '../user-preferences/user-portfolio-preferences.service';
import { UserProfileService } from 'app/shared/user-profile/user-profile.service';
import { NavigationCategory, NavigationService } from '../data-services/navigation/navigation.service';
import { UserPreferencesService } from 'app/shared/services-api-central/user-preferences/user-preferences.service';
import { PrivacyConsentEnum } from 'app/shared/notifications-api/graphql';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class UserStatsService implements OnDestroy {
  private readonly SHOULD_DEBUG: boolean = false;
  private readonly APP: string = 'Wind';

  private isInternalUser: boolean = false;
  private hasPolicyConsent: boolean = false;
  private darkModeEnabled: boolean = false;

  private previousUrl: string;
  private previousLevel: string;
  private currentLevel: string;
  private nextLevel: string;
  private previousModule: string;
  private currentModule: string;
  private nextModule: string;
  private previousAssetName: string;
  private currentAssetName: string;
  private nextAssetName: string;
  private page: string;
  private user: any;
  private userProfile: any;
  private unsubscriber: ReplaySubject<boolean> = new ReplaySubject();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private navigationService: NavigationService,
    private navigationDataService: NavigationDataService,
    private userProfileService: UserProfileService,
    private userPortfolioService: UserPortfolioPreferencesService,
    private userPreferencesService: UserPreferencesService,
  ) {
    this.initializeMixpanel();
    this.initializeNavigationTracking(router);
  }

  private initializeMixpanel(): void {
    this.getUserData();
    mixpanel.init(environment.mixpanel_token, {
      debug: !environment.production && this.SHOULD_DEBUG,
      track_pageview: false,
      persistence: 'localStorage',
    });
    this.registerMixpanelSuperProperties();
  }

  private canTrack(): boolean {
    return !this.isInternalUser && this.hasPolicyConsent;
  }

  private initializeNavigationTracking(router: Router): void {
    this.previousUrl = router.url;

    const navigationObservable$ = this.navigationService.getNavigationObservable([
      NavigationCategory.Level,
      NavigationCategory.Module,
      NavigationCategory.PortfolioLevelSelectedPortfolio,
      NavigationCategory.ParkLevelSelectedPark,
      NavigationCategory.TurbineLevelSelectedTurbine,
    ]);

    const navigationEnd$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => event as NavigationEnd),
    );

    mixpanel.time_event('page_view');

    combineLatest([navigationObservable$, navigationEnd$])
      .pipe(
        takeUntil(this.unsubscriber),
        map(([navDetails, navigationEndEvent]) => {
          this.currentAssetName = this.getAssetName(navDetails);
          this.currentLevel = navDetails.level;
          this.currentModule = navDetails.module;
          this.nextLevel = this.currentLevel;
          this.nextModule = this.currentModule;
          this.nextAssetName = this.currentAssetName;

          return {
            urlAfterRedirects: navigationEndEvent.urlAfterRedirects,
            asset_level: this.previousLevel || this.currentLevel,
            module: this.previousModule || this.currentModule,
            asset_name: this.previousAssetName || this.currentAssetName,
          };
        }),
      )
      .subscribe(({ urlAfterRedirects, asset_level, module, asset_name }) => {
        if (!this.canTrack() || urlAfterRedirects === this.page) {
          return;
        }

        const isInitialPageLoad = !this.page;

        this.registerMixpanelSuperProperties({ referrer: this.previousUrl });

        if (this.page) {
          mixpanel.track('page_view', {
            ...this.getCustomProperties(),
            page: this.page,
            asset_level: this.previousLevel,
            module: this.previousModule,
            asset_name: this.previousAssetName,
            beforeUnload: false,
          });
        }

        if (!isInitialPageLoad && window.dataLayer) {
          window.dataLayer.push({
            event: 'virt_path',
            page_path: urlAfterRedirects,
            page_title: this.document.title,
            page_category: asset_level,
          });
        }

        this.previousUrl = this.page;
        this.previousLevel = this.currentLevel;
        this.previousModule = this.currentModule;
        this.previousAssetName = this.currentAssetName;

        this.page = urlAfterRedirects;
        this.currentLevel = asset_level;
        this.currentModule = module;
        this.currentAssetName = asset_name;

        mixpanel.time_event('page_view');
      });
  }

  private getUserData(): void {
    this.userPortfolioService.getUserInfo().subscribe(({ data: { user } }) => {
      this.user = user;
    });
    this.userProfile = this.userProfileService.getUserProfile();

    this.userPreferencesService
      .getUserPreferencesLoaded()
      .pipe(
        filter(v => v),
        takeUntil(this.unsubscriber),
      )
      .subscribe(() => {
        const prefs = this.userPreferencesService.getUserPreferences();
        this.hasPolicyConsent = prefs?.privacyConsent === PrivacyConsentEnum.OptedIn;
        this.updateDarkModePreference();
        this.tryInitTrackingScripts();
      });
  }

  private getAssetName(navDetails: any): string {
    if (navDetails.level === 'portfolio') {
      return this.navigationDataService.getSubPortfolioListData()?.value?.selectedPortfolio?.name || 'Main Portfolio';
    } else if (navDetails.level === 'park') {
      return this.navigationDataService.getParkNameForNavigationById(navDetails.selectedParkId);
    } else {
      return this.navigationDataService.getTurbineById(navDetails.selectedTurbineId)?.code;
    }
  }

  private getCustomProperties() {
    return {
      product: this.APP,
      position: this.user?.position || 'Not defined',
      company: this.userProfile?.attributes?.company?.[0] || 'Not defined',
    };
  }

  private registerMixpanelSuperProperties(properties: any = {}): void {
    if (!this.hasPolicyConsent) return;
    mixpanel.register(properties);
  }

  private injectScript(src: string): HTMLScriptElement {
    const scriptElement = this.document.createElement('script');
    scriptElement.async = true;
    scriptElement.src = src;

    const firstScript = this.document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(scriptElement, firstScript);
    } else {
      this.document.head.appendChild(scriptElement);
    }

    return scriptElement;
  }

  public loadUser(isInternalUser: boolean): void {
    this.isInternalUser = isInternalUser;
    this.tryInitTrackingScripts();
  }

  // uncomment this function once tested
  // private tryInitTrackingScripts(): void {
  //   if (!this.canTrack() || !environment.production) return;
  //   this.initClarity();
  //   this.initGTM();
  // }

  // EQF: temporal change to test consent in dev
  private tryInitTrackingScripts(): void {
  if (!this.canTrack()) return;

  this.initClarity();

  if (environment.production) {
    this.initGTM();
  }
}

  private initClarity(): void {
    if ((window as any).clarity) {
      return;
    }
    const clarityId = environment.clarity_token;

    (window as any).clarity =
      (window as any).clarity ||
      function () {
        ((window as any).clarity.q = (window as any).clarity.q || []).push(arguments);
      };

    (window as any).clarity('consentv2', {
      ad_Storage: 'granted',
      analytics_Storage: 'granted',
    });

    this.injectScript(`https://www.clarity.ms/tag/${clarityId}`);
  }

  private initGTM(): void {
    const gtmId = environment.gtm_token;

    if ((window as any).__gtmInjected) {
      return;
    }

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'page_meta',
      website_type: 'main_website',
      content_type: 'application',
    });

    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    this.injectScript(`https://www.googletagmanager.com/gtm.js?id=${gtmId}`);

    const noscriptElement = this.document.createElement('noscript');
    const iframeElement = this.document.createElement('iframe');
    iframeElement.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframeElement.height = '0';
    iframeElement.width = '0';
    iframeElement.style.display = 'none';
    iframeElement.style.visibility = 'hidden';
    noscriptElement.appendChild(iframeElement);

    this.document.body.insertBefore(noscriptElement, this.document.body.firstChild);

    (window as any).__gtmInjected = true;
  }

  private updateDarkModePreference(): void {
    const prefs = this.userPreferencesService.getUserPreferences();
    const theme = prefs?.theme || 'LIGHT';
    this.darkModeEnabled = theme === 'DARK';

    if (window.gtag && this.canTrack()) {
      window.gtag('set', 'user_properties', {
        dark_mode_enabled: this.darkModeEnabled,
      });
    }
  }

  public login(userId: string): void {
    if (!this.canTrack()) return;
    mixpanel.identify(userId);
    mixpanel.track('Login', this.getCustomProperties());
  }

  public logout(): void {
    if (!this.canTrack()) return;
    this.trackBeforeUnload();
    mixpanel.track('Logout', this.getCustomProperties());
    this.isInternalUser = false;
  }

  public sendCustomEvent(eventName: string, assetLevel: string, name: string): void {
    if (!this.canTrack()) return;

    const props = {
      ...this.getCustomProperties(),
      asset_name: name,
      asset_level: assetLevel,
    };

    mixpanel.track(eventName, props);
    mixpanel.time_event(eventName);
  }

  public trackBeforeUnload(): void {
    if (!this.canTrack()) return;

    mixpanel.track('page_view', {
      ...this.getCustomProperties(),
      page: this.page,
      asset_level: this.nextLevel,
      module: this.nextModule,
      asset_name: this.nextAssetName,
      beforeUnload: true,
    });

    mixpanel.time_event('page_view');
  }

  ngOnDestroy(): void {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }
}
